// Knowledge base index.
//
// Articles are static HTML files in public/help/, served by the web server and
// never parsed by React. Nothing here is bundled with an article: this module
// only fetches the manifest and ranks it, so the whole knowledge base costs the
// app zero bytes until somebody opens the Help panel.
//
// Deliberately NOT a React hook and deliberately tiny. The Help panel is lazy
// loaded, but the Support modal also searches the index, and it must be able to
// do that without pulling the panel's chunk down with it.

const BASE = `${process.env.PUBLIC_URL || ""}/help`;

// Module-scoped so the second open of the panel, and the Support modal after
// it, are instant. The manifest is small and changes on deploy, not in session.
let cache = null;
let inFlight = null;

export function articleUrl(file) {
  return `${BASE}/${file}`;
}

/** The manifest, fetched at most once per page load. */
export function loadArticles() {
  if (cache) return Promise.resolve(cache);
  if (inFlight) return inFlight;

  inFlight = fetch(`${BASE}/index.json`)
    .then((res) => {
      if (!res.ok) throw new Error(`Help index returned ${res.status}`);
      return res.json();
    })
    .then((data) => {
      cache = Array.isArray(data?.articles) ? data.articles : [];
      inFlight = null;
      return cache;
    })
    .catch((err) => {
      // Not cached, so the next open retries rather than showing an empty
      // knowledge base forever because one request failed.
      inFlight = null;
      throw err;
    });

  return inFlight;
}

/** The app's current hash route, without the leading # or any query string. */
export function currentRoute() {
  return String(window.location.hash || "")
    .replace(/^#/, "")
    .split("?")[0];
}

/**
 * Articles tagged for the screen the user is on.
 *
 * This is what makes the panel worth opening. A help drawer that always starts
 * on a generic index gets opened once; one that already has the article for the
 * screen you are stuck on gets used.
 */
export function articlesForRoute(articles, route = currentRoute()) {
  if (!route) return [];
  return articles.filter((a) =>
    (a.routes || []).some((prefix) => route.startsWith(prefix)),
  );
}

function haystack(article) {
  return [article.title, article.summary, ...(article.tags || [])]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

/**
 * Rank articles against a query.
 *
 * Substring matching over the manifest, scored so a title hit outranks a tag
 * hit. At a few hundred articles this is still instant and needs no service;
 * when it stops being enough, the manifest is the thing to index, not this.
 */
export function searchArticles(articles, query) {
  const terms = String(query || "")
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

  if (!terms.length) return articles;

  return articles
    .map((article) => {
      const title = String(article.title || "").toLowerCase();
      const tags = (article.tags || []).map((t) => String(t).toLowerCase());
      const all = haystack(article);

      let score = 0;
      for (const term of terms) {
        // Every term has to appear somewhere, so a two-word query narrows
        // rather than widening the way an OR match would.
        if (!all.includes(term)) return null;
        if (title.includes(term)) score += 3;
        else if (tags.some((t) => t.includes(term))) score += 2;
        else score += 1;
      }
      return { article, score };
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score)
    .map((hit) => hit.article);
}
